<script lang="ts" setup>
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { bgFooterModal, bgModal } from '@/Support/Styles/Global/ModalStyles'
import Button from '@/Components/App/Forms/Button.vue'
import { OhVueIcon } from 'oh-vue-icons'
import { storeToRefs } from 'pinia'
import { useAlertModal } from '@/stores/alertModal'
import { t } from '@/Support/lang'
import { computed, ref } from 'vue'
import { getAppIcon } from '@/Support/Common/Interfaces/appIcons'
import AppIcon from '@/Components/App/Global/AppIcon.vue'
import Input from '@/Components/App/Forms/Input.vue'
import TextArea from '@/Components/App/Forms/TextArea.vue'
import { text } from 'linkifyjs'

const alertModal = useAlertModal()
const {
  title,
  description,
  primaryButtonAction,
  primaryButtonLabel,
  cancelButtonAction,
  cancelButtonLabel,
  closeOnPrimary,
  twoButtonsCentered,
  centered,
  bgCenteredIconClass,
  bgIconClass,
  show,
  wide,
  disabled,
  onSubmit,
  icon,
  iconClass,
  inputTexts,
  textAreas,
} = storeToRefs(alertModal)
const { resetState } = alertModal
const inputTextFields = ref(inputTexts)
const textAreaFields = ref(textAreas)

function close() {
  show.value = false
}

/**
 * Submit
 */
const submit = () => {
  if (onSubmit?.value) {
    const inputs = inputTextFields.value?.reduce(
      (acc, input) => ({
        ...acc,
        [input.key]: input.value,
      }),
      {},
    )

    const textAreas = textAreaFields.value?.reduce(
      (acc, input) => ({
        ...acc,
        [input.key]: input.value,
      }),
      {},
    )
    onSubmit.value(inputs, textAreas)
    onSubmit.value = () => {}
    close()
  }

  if (closeOnPrimary?.value) {
    close()
  }
}

const descriptionParsed = computed(() => {
  const text = description?.value
  if (!text || !text.includes(`\n`)) {
    return text
  }

  return text.replaceAll(`\n`, '<br>')
})
</script>
<template>
  <TransitionRoot appear as="template" :show="show">
    <Dialog as="div" class="relative z-30" @close="close">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
        @after-leave="resetState"
      >
        <div :class="bgModal" />
      </TransitionChild>

      <div class="fixed z-30 inset-0 overflow-y-auto">
        <div
          class="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm backdrop-filter rounded-lg text-left xxxoverflow-hidden shadow-2xl transform transition-all sm:my-8 mx-1 sm:mx-0"
              :class="
                wide ? 'max-w-sm sm:max-w-lg w-full' : 'sm:w-full sm:max-w-sm'
              "
            >
              <div
                class="px-4 pb-4 sm:pb-4"
                :class="centered ? 'pt-5 sm:p-3' : 'sm:p-6 pt-5'"
              >
                <!-- CENTERED -->
                <div v-if="centered" class="pt-3">
                  <div :class="bgCenteredIconClass">
                    <slot name="icon" :iconclass="iconClass">
                      <AppIcon :icon="icon ?? 'success'" :class="iconClass" />
                    </slot>
                  </div>

                  <div class="mt-3 text-center sm:mt-5">
                    <DialogTitle
                      as="h3"
                      class="text-base font-semibold leading-6 text-neutral-900 dark:text-neutral-100"
                    >
                      {{ t(title) }}
                    </DialogTitle>
                    <div v-if="description" class="mt-2">
                      <p
                        class="text-sm text-neutral-500 dark:text-neutral-300 px-2"
                        v-html="descriptionParsed"
                      ></p>
                    </div>
                  </div>
                </div>

                <!-- RIGHT -->
                <div v-else class="sm:flex sm:items-start py-3">
                  <div :class="bgIconClass">
                    <slot name="icon" :iconclass="iconClass">
                      <AppIcon :icon="icon ?? 'success'" :class="iconClass" />
                    </slot>
                  </div>
                  <div
                    class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full"
                  >
                    <DialogTitle
                      as="h3"
                      class="text-base leading-6 font-medium text-neutral-900 dark:text-neutral-100"
                    >
                      {{ t(title) }}
                    </DialogTitle>
                    <div v-if="description" class="mt-2">
                      <p class="text-sm text-neutral-500 dark:text-neutral-300">
                        {{ t(description) }}
                      </p>
                    </div>
                    <div class="mt-4">
                      <Input
                        v-for="inputText in inputTextFields"
                        :placeholder="t(inputText.placeholder)"
                        v-model="inputText.value"
                        class="block w-full"
                        required
                        type="text"
                      />
                      <div
                        v-if="inputTextFields?.length && textAreaFields?.length"
                        class="h-3"
                      ></div>
                      <TextArea
                        v-for="textArea in textAreaFields"
                        v-model="textArea.value"
                        :placeholder="t(textArea.placeholder)"
                        class="border-none disabled:bg-neutral-100 dark:bg-neutral-500"
                        type="text"
                        :rows="textArea.rows ?? 3"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <slot name="footer">
                <!-- CENTERED -->
                <div
                  v-if="centered"
                  class="px-4 pt-5 pb-4 sm:p-6 sm:pt-3 sm:pb-4 mb-2"
                  :class="{
                    'sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3':
                      twoButtonsCentered,
                  }"
                >
                  <Button
                    v-if="twoButtonsCentered"
                    :action="cancelButtonAction"
                    @click="close"
                  >
                    {{ t(cancelButtonLabel) }}
                  </Button>

                  <Button
                    full-width
                    :action="primaryButtonAction"
                    :disabled="disabled"
                    @click="submit"
                  >
                    <OhVueIcon
                      v-if="disabled"
                      name="la-spinner-solid"
                      class="h-5 w-5 animate-spin"
                    />
                    <span v-else>
                      {{ t(primaryButtonLabel) }}
                    </span>
                  </Button>
                </div>

                <!-- RIGHT -->
                <div v-else :class="bgFooterModal">
                  <Button
                    :action="primaryButtonAction"
                    :disabled="disabled"
                    @click="submit"
                  >
                    <OhVueIcon
                      v-if="disabled"
                      name="la-spinner-solid"
                      class="h-5 w-5 animate-spin"
                    />
                    <span v-else>
                      {{ t(primaryButtonLabel) }}
                    </span>
                  </Button>

                  <Button
                    :action="cancelButtonAction"
                    class="mr-2 mt-3 sm:mt-0"
                    @click="close"
                  >
                    {{ t(cancelButtonLabel) }}
                  </Button>
                </div>
              </slot>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
